import React, { useRef } from "react";
import { useReveal } from "../hooks/useReveal";

const FOOTER_COPY = {
  pt: {
    callNote: "Chamada para a rede fixa nacional",
    legalNotice: "Aviso Legal",
    cookiesPolicy: "Política de Cookies",
    privacyPolicy: "Política de Privacidade",
    coaching40: "Coaching 4.0",
    backToTop: "Voltar ao topo",
    supports: "Apoios",
    institutionalTitle: "Apoio Institucional",
    institutionalDescription: "Projeto apoiado no âmbito do Plano de Recuperação e Resiliência",
    institutionalLinkLabel: "Visitar recuperarportugal.gov.pt",
    institutionalLinkUrl: "https://www.recuperarportugal.gov.pt",
    institutionalSupportAlt:
      "Logótipos: PRR - Plano de Recuperação e Resiliência, República Portuguesa, União Europeia - NextGeneration EU",
  },
  en: {
    callNote: "Call to the national landline network",
    legalNotice: "Legal Notice",
    cookiesPolicy: "Cookie Policy",
    privacyPolicy: "Privacy Policy",
    coaching40: "Coaching 4.0",
    backToTop: "Back to top",
    supports: "Supported by",
    institutionalTitle: "Institutional Support",
    institutionalDescription: "Project supported under the Recovery and Resilience Plan",
    institutionalLinkLabel: "Visit recuperarportugal.gov.pt",
    institutionalLinkUrl: "https://www.recuperarportugal.gov.pt",
    institutionalSupportAlt:
      "Logos: PRR - Recovery and Resilience Plan, Portuguese Republic, European Union - NextGeneration EU",
  },
};

export default function Footer({ lang = "pt", variant = "default" }) {
  const brand = "#4CB1A1";
  const ink = "var(--ink)";
  const muted = "var(--muted)";

  const isCoachingFooter = variant === "coaching40";

  // scroll suave para o topo
  const goTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const copy = FOOTER_COPY[lang] || FOOTER_COPY.pt;

  const contactRef = useRef(null);
  const legalRef = useRef(null);
  const socialsRef = useRef(null);
  const supportRef = useRef(null);
  const bottomRef = useRef(null);
  const fundingRef = useRef(null);
  const institutionalRef = useRef(null);

  useReveal(contactRef, { threshold: 0.15 });
  useReveal(legalRef, { threshold: 0.2 });
  useReveal(socialsRef, { threshold: 0.2 });
  useReveal(supportRef, { threshold: 0.2 });
  useReveal(bottomRef, { threshold: 0.1 });
  useReveal(fundingRef, { threshold: 0.2 });
  useReveal(institutionalRef, { threshold: 0.2 });

  return (
    <footer id="footer" className="w-full bg-white scroll-mt-32 sm:scroll-mt-36 lg:scroll-mt-40">
      {/* bloco principal */}
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          {/* logo */}
          <div className="md:col-span-3 flex justify-center md:justify-start">
            <picture>
              <source media="(prefers-color-scheme: dark)" srcSet="/assets/logoWhite.png" />
              <img src="/assets/logo.png" alt="Invisible Cloud" className="h-12 w-auto sm:h-14" />
            </picture>
          </div>

          {/* contactos */}
          <div ref={contactRef} className="md:col-span-4">
            <ul className="space-y-5 text-[15px]">
              <li className="flex items-start gap-3">
                <GeoIcon className="mt-0.5 shrink-0" color={brand} />
                <a
                  href="https://maps.google.com/?q=Rua%20Alfredo%20Allen%20455%2F461,%204200-135%20Porto,%20Portugal"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-slate-700 leading-snug hover:underline"
                >
                  <span className="block">Rua Alfredo Allen 455/461</span>
                  <span className="block">4200-135 Porto, Portugal</span>
                </a>
              </li>
              <li className="flex items-start gap-3">
                <PhoneIcon className="mt-0.5 shrink-0" color={brand} />
                <div className="text-slate-700 leading-snug">
                  <a href="tel:+351221450660" className="text-slate-700 hover:underline">
                    +351 221 450 660
                  </a>
                  <span className="mt-0.5 block text-slate-400 text-[10px]" style={{ fontFamily: "Inter, sans-serif" }}>
                    {copy.callNote}
                  </span>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <MailIcon className="shrink-0" color={brand} />
                <a href="mailto:info@invisiblecloud.pt" className="text-slate-700 hover:underline">
                  info@invisiblecloud.pt
                </a>
              </li>
            </ul>
          </div>

          {/* legal + social */}
          <div className="md:col-span-3 flex flex-col gap-5 text-left">
            <nav ref={legalRef} className="space-y-1.5 text-[15px]">
              <a href="/aviso-legal" className="block hover:underline" style={{ color: ink }}>
                {copy.legalNotice}
              </a>
              <a href="/politica-cookies" className="block hover:underline" style={{ color: ink }}>
                {copy.cookiesPolicy}
              </a>
              <a href="/politica-privacidade" className="block hover:underline" style={{ color: ink }}>
                {copy.privacyPolicy}
              </a>
              <a href="/coaching4.0" className="block hover:underline" style={{ color: ink }}>
                {copy.coaching40}
              </a>
            </nav>

            <div ref={socialsRef} className="flex w-full items-end justify-between gap-3 pt-1 text-slate-800 md:items-center">
              <div className="flex items-center gap-3">
                <a href="https://pt.linkedin.com/company/invisible-cloud" aria-label="LinkedIn" className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition">
                  <LinkedInIcon alt="LinkedIn" />
                </a>
                <a href="https://x.com/InviCloud" aria-label="X (Twitter)" className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition">
                  <TwitterXIcon alt="X (Twitter)" />
                </a>
                <a href="https://www.facebook.com/invisiblecloudlda/" aria-label="Facebook" className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition">
                  <FacebookIcon alt="Facebook" />
                </a>
              </div>
              <img
                src="/assets/ral.jpg"
                alt="RAL compliance badge"
                className="ml-auto block w-full max-w-[90px] rounded-lg border border-slate-200 bg-white p-2 object-contain self-end md:hidden"
                loading="lazy"
              />
            </div>
          </div>

          {/* seta topo + RAL */}
          <div className="md:col-span-2 flex flex-col items-center gap-5 md:items-center">
            <button
              onClick={goTop}
              aria-label={copy.backToTop}
              className="inline-flex h-12 w-12 items-center justify-center rounded-full text-white transition hover:opacity-90"
              style={{ backgroundColor: brand }}
              title={copy.backToTop}
            >
              <UpIcon />
            </button>
            <img
              src="/assets/ral.jpg"
              alt="RAL compliance badge"
              className="hidden w-full max-w-[90px] rounded-lg border border-slate-200 bg-white p-3 object-contain self-start sm:max-w-[100px] md:block md:max-w-[110px] lg:max-w-[120px]"
              loading="lazy"
            />
          </div>
        </div>
      </div>

      {/* faixa de apoios */}
      <div className="border-t border-slate-200">
        <div
          ref={supportRef}
          className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8 py-6"
        >
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-6">
            <span className="text-slate-500 text-left sm:min-w-[120px]">
              {copy.supports}
            </span>
            <div className="flex w-full flex-wrap items-center justify-start gap-6 sm:w-auto sm:gap-10 lg:gap-12">
              <picture>
                <source media="(prefers-color-scheme: dark)" srcSet="/assets/aws_white.png" />
                <img
                  src="/assets/aws.png"
                  alt="AWS Partner"
                  className="h-12 w-auto object-contain sm:h-14 lg:h-16"
                  loading="lazy"
                />
              </picture>
              <img
                src="/assets/spinoff_0.png"
                alt="Spin-off U.Porto"
                className="h-12 w-auto object-contain sm:h-14 lg:h-16"
                loading="lazy"
              />
              <picture>
                <source media="(prefers-color-scheme: dark)" srcSet="/assets/uptec_white.svg" />
                <img
                  src="/assets/uptec.png"
                  alt="UPTEC"
                  className="h-8 w-auto object-contain sm:h-9 lg:h-10"
                  loading="lazy"
                />
              </picture>
            </div>
          </div>
        </div>
      </div>
      {!isCoachingFooter && (
        <>
          <div className="border-t border-slate-200">
            <div ref={fundingRef} className="w-full bg-[#F0FAF8] py-12">
              <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
                <div className="flex flex-col items-start gap-6 lg:flex-row lg:items-center lg:gap-10">
                  <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-[#1F8483] text-white shadow-lg">
                    <svg className="h-8 w-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                    </svg>
                  </div>
                  <div className="max-w-3xl space-y-4">
                    <h2 className="text-2xl font-bold text-[#0D2D2A] sm:text-3xl">
                      {copy.institutionalTitle}
                    </h2>
                    <p className="text-lg text-[#2F3B3A]">
                      {copy.institutionalDescription}
                    </p>
                  </div>
                  <a
                    href={copy.institutionalLinkUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 rounded-full bg-[#1F8483] px-8 py-4 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:-translate-y-1 hover:bg-[#176865] hover:shadow-xl"
                  >
                    {copy.institutionalLinkLabel}
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-200">
            <div ref={institutionalRef} className="w-full bg-white py-8">
              <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-0">
                <img
                  src="/assets/apoiosC.png"
                  alt={copy.institutionalSupportAlt}
                  className="w-full h-auto object-contain"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* barra final */}
      <div className="border-t border-slate-200">
        <div
          ref={bottomRef}
          className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8 py-4 text-center text-sm"
          style={{ color: muted }}
        >
          Copyright © {new Date().getFullYear()} · Invisible Cloud
        </div>
      </div>
    </footer>
  );
}

/* ---------- SVGs ---------- */

function GeoIcon({ className = "", color = "#000" }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill={color} className={`bi bi-geo-alt-fill ${className}`} viewBox="0 0 16 16" aria-hidden="true">
      <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10m0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6"/>
    </svg>
  );
}

function PhoneIcon({ className = "", color = "#000" }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill={color} className={`bi bi-telephone-fill ${className}`} viewBox="0 0 16 16" aria-hidden="true">
      <path fillRule="evenodd" d="M1.885.511a1.745 1.745 0 0 1 2.61.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.68.68 0 0 0 .178.643l2.457 2.457a.68.68 0 0 0 .644.178l2.189-.547a1.75 1.75 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.6 18.6 0 0 1-7.01-4.42 18.6 18.6 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877z"/>
    </svg>
  );
}

function MailIcon({ className = "", color = "#000" }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill={color} className={`bi bi-envelope-open-fill ${className}`} viewBox="0 0 16 16" aria-hidden="true">
      <path d="M8.941.435a2 2 0 0 0-1.882 0l-6 3.2A2 2 0 0 0 0 5.4v.314l6.709 3.932L8 8.928l1.291.718L16 5.714V5.4a2 2 0 0 0-1.059-1.765zM16 6.873l-5.693 3.337L16 13.372v-6.5Zm-.059 7.611L8 10.072.059 14.484A2 2 0 0 0 2 16h12a2 2 0 0 0 1.941-1.516M0 13.373l5.693-3.163L0 6.873z"/>
    </svg>
  );
}

function LinkedInIcon({ alt = "LinkedIn" }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-linkedin" viewBox="0 0 16 16" role="img" aria-label={alt}>
      <title>{alt}</title>
      <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854zm4.943 12.248V6.169H2.542v7.225zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248S2.4 3.226 2.4 3.934c0 .694.521 1.248 1.327 1.248zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016l.016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225z"/>
    </svg>
  );
}

function TwitterXIcon({ alt = "X (Twitter)" }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-twitter-x" viewBox="0 0 16 16" role="img" aria-label={alt}>
      <title>{alt}</title>
      <path d="M12.6.75h2.454l-5.36 6.142L16 15.25h-4.937l-3.867-5.07-4.425 5.07H.316l5.733-6.57L0 .75h5.063l3.495 4.633L12.601.75Zm-.86 13.028h1.36L4.323 2.145H2.865z"/>
    </svg>
  );
}

function FacebookIcon({ alt = "Facebook" }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-facebook" viewBox="0 0 16 16" role="img" aria-label={alt}>
      <title>{alt}</title>
      <path d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951"/>
    </svg>
  );
}

function UpIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 19V5M5 12l7-7 7 7" />
    </svg>
  );
}
